import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { 
  getCurrentUserProfile
} from '../api/profileApi';
import api from '../../../api';
import useModalEffect from '../../../hooks/useModalEffect';

const EditProfileModal = ({ isOpen, onClose, onSuccess }) => {
  useModalEffect(isOpen);
  const [formData, setFormData] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    nohp: '',
    address: '',
    country: '',
    state: '',
    city: '',
    postalCode: ''
  });

  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [profilePreview, setProfilePreview] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [profileFile, setProfileFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);

  // Options for dropdowns (removed - no longer needed)

  const hasPopulatedForm = useRef(false);

  // Load options on component mount (removed - no longer needed)

  // Handle modal open/close
  ;

  // Load user profile data when modal opens
  useEffect(() => {
    if (isOpen && !hasPopulatedForm.current) {
      hasPopulatedForm.current = true;
      loadUserProfile();
    }
  }, [isOpen]);

  // Reset the flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasPopulatedForm.current = false;
    }
  }, [isOpen]);

  const loadUserProfile = async () => {
    try {
      setIsLoading(true);
      const userData = await getCurrentUserProfile();
      
      setFormData({
        username: userData.username || '',
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        nohp: userData.nohp || '',
        address: userData.address || '',
        country: userData.country || '',
        state: userData.state || '',
        city: userData.city || '',
        postalCode: userData.postalCode || ''
      });

      // Set profile picture preview if exists
      if (userData.profilePicture) {
        const apiUrl = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:8080';
        setProfilePreview(`${apiUrl}/api/public/displayimage/${userData.profilePicture}`);
      }

      // Set signature preview if exists
      if (userData.signature) {
        const apiUrl = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:8080';
        setSignaturePreview(`${apiUrl}/api/public/displayimage/${userData.signature}`);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };


  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    console.log('=== PROFILE FILE SELECT DEBUG ===');
    console.log('File selected:', file);
    console.log('File name:', file?.name);
    console.log('File size:', file?.size);
    console.log('File type:', file?.type);
    console.log('=== END PROFILE FILE SELECT DEBUG ===');
    
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, profilePicture: 'File size must be less than 5MB' }));
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, profilePicture: 'Please select a valid image file' }));
        return;
      }

      console.log('Setting profile file:', file.name);
      setProfileFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePreview(e.target.result);
        console.log('Profile preview set successfully');
      };
      reader.readAsDataURL(file);
    } else {
      console.log('No file selected');
    }
  };

  const handleSignatureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, signature: 'File size must be less than 2MB' }));
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, signature: 'Please select a valid image file' }));
        return;
      }

      setSignatureFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setSignaturePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeProfilePicture = () => {
    setProfileFile(null);
    setProfilePreview(null);
    const input = document.getElementById('profile-picture-input-edit');
    if (input) input.value = '';
  };

  const removeSignature = () => {
    setSignatureFile(null);
    setSignaturePreview(null);
    const input = document.getElementById('signature-input-edit');
    if (input) input.value = '';
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) newErrors.username = 'Username is required';
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.nohp.trim()) newErrors.nohp = 'Phone number is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.postalCode.trim()) newErrors.postalCode = 'Postal code is required';

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      // Get current user ID first (like InternalUsers.js pattern)
      const currentUser = await getCurrentUserProfile();
      const userId = currentUser.id;

      if (!userId) {
        alert('Error: User ID not found. Please refresh the page and try again.');
        setIsSaving(false);
        return;
      }

      // Create FormData for CRM profile endpoint (simplified - no employee ID validation)
      const formDataToSend = new FormData();

      // Add form data for CRM profile endpoint
      formDataToSend.append('username', formData.username || '');
      formDataToSend.append('firstName', formData.firstName || '');
      formDataToSend.append('lastName', formData.lastName || '');
      formDataToSend.append('email', formData.email || '');
      formDataToSend.append('nohp', formData.nohp || '');
      formDataToSend.append('address', formData.address || '');
      formDataToSend.append('country', formData.country || '');
      formDataToSend.append('state', formData.state || '');
      formDataToSend.append('city', formData.city || '');
      formDataToSend.append('postalCode', formData.postalCode || '');

      // Add files if they exist (like InternalUsers.js)
      if (profileFile) {
        console.log('Adding profile picture to FormData...');
        formDataToSend.append('profilePicture', profileFile);
      }
      if (signatureFile) {
        console.log('Adding signature to FormData...');
        formDataToSend.append('signature', signatureFile);
      }

      // Debug: Log form data (like InternalUsers.js)
      console.log('=== FILE UPLOAD DEBUG ===');
      console.log('Form data being sent:', formData);
      console.log('Profile file:', profileFile);
      console.log('Profile file name:', profileFile?.name);
      console.log('Profile file size:', profileFile?.size);
      console.log('Signature file:', signatureFile);
      console.log('Signature file name:', signatureFile?.name);
      console.log('Signature file size:', signatureFile?.size);
      console.log('=== END FILE UPLOAD DEBUG ===');

      // Send single request with FormData using the CRM profile endpoint
      const response = await api.put('/crm/profile', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('=== BACKEND RESPONSE DEBUG ===');
      console.log('Full response:', response);
      console.log('Response data:', response.data);
      console.log('Success:', response.data?.success);
      console.log('Message:', response.data?.message);
      console.log('=== END BACKEND RESPONSE DEBUG ===');

      if (response.data && response.data.success) {
        // Show success message
        alert('Profile updated successfully!');
        
        // Close modal and notify success
        if (onSuccess) {
          onSuccess(); // Call onSuccess before closing modal
        }
        handleClose();
      } else {
        console.error('Unexpected response format:', response.data);
        alert(response.data?.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      if (error.response?.data?.error) {
        alert('Error: ' + error.response.data.error);
      } else {
        alert('Error updating profile. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    
    
    if (onClose) onClose();
  };


  return (
    <div className={`modal modal-blur fade${isOpen ? ' show' : ''}`} style={{ display: isOpen ? 'block' : 'none' }} id="modal-edit-profile" tabIndex="-1" role="dialog">
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Edit Profile</h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose} 
              aria-label="Close" 
              style={{ pointerEvents: isSaving ? 'none' : 'auto', opacity: isSaving ? 0.6 : 1 }}
            ></button>
          </div>
          
          {isLoading ? (
            <div className="modal-body">
              <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading profile data...</span>
                </div>
                <p className="mt-3 mb-0">Loading profile data...</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* File Upload Section */}
                <h3 className="card-title mt-4">Profile & Signature</h3>
                <div className="row g-3 mb-4">
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Profile Picture</label>
                      <input
                        type="file"
                        className="form-control"
                        id="profile-picture-input-edit"
                        accept="image/*"
                        onChange={handleProfilePictureChange}
                      />
                      <div className="form-text">Max size: 5MB. Supported formats: JPG, PNG, GIF</div>
                      {profilePreview && (
                        <div className="mt-2">
                          <div className="d-flex align-items-center">
                            <img
                              src={profilePreview}
                              alt="Profile preview"
                              className="border rounded"
                              style={{ 
                                maxWidth: '150px', 
                                maxHeight: '150px',
                                objectFit: 'cover',
                                cursor: 'default'
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger ms-2"
                              onClick={removeProfilePicture}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Signature</label>
                      <input
                        type="file"
                        className="form-control"
                        id="signature-input-edit"
                        accept="image/*"
                        onChange={handleSignatureChange}
                      />
                      <div className="form-text">Max size: 2MB. Supported formats: JPG, PNG, GIF</div>
                      {signaturePreview && (
                        <div className="mt-2">
                          <div className="d-flex align-items-center">
                            <img
                              src={signaturePreview}
                              alt="Signature preview"
                              className="border rounded"
                              style={{ 
                                maxWidth: '150px', 
                                maxHeight: '80px',
                                objectFit: 'cover',
                                cursor: 'default'
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger ms-2"
                              onClick={removeSignature}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Username <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                        name="username"
                        value={formData.username}
                        onChange={(e) => handleInputChange('username', e.target.value)}
                        placeholder="Enter username"
                      />
                      {errors.username && <div className="invalid-feedback">{errors.username}</div>}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">First Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                        name="firstName"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        placeholder="Enter first name"
                      />
                      {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Last Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                        name="lastName"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        placeholder="Enter last name"
                      />
                      {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Email <span className="text-danger">*</span></label>
                      <input
                        type="email"
                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                        name="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="Enter email address"
                      />
                      {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Phone Number <span className="text-danger">*</span></label>
                      <input
                        type="tel"
                        className={`form-control ${errors.nohp ? 'is-invalid' : ''}`}
                        name="nohp"
                        value={formData.nohp}
                        onChange={(e) => handleInputChange('nohp', e.target.value)}
                        placeholder="Enter phone number"
                      />
                      {errors.nohp && <div className="invalid-feedback">{errors.nohp}</div>}
                    </div>
                  </div>

                  <div className="col-lg-12">
                    <div className="mb-3">
                      <label className="form-label">Address <span className="text-danger">*</span></label>
                      <textarea
                        className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                        name="address"
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        placeholder="Enter complete address"
                        rows="3"
                      />
                      {errors.address && <div className="invalid-feedback">{errors.address}</div>}
                    </div>
                  </div>

                  <div className="col-lg-4">
                    <div className="mb-3">
                      <label className="form-label">Country</label>
                      <input
                        type="text"
                        className={`form-control ${errors.country ? 'is-invalid' : ''}`}
                        name="country"
                        value={formData.country}
                        onChange={(e) => handleInputChange('country', e.target.value)}
                        placeholder="Country"
                      />
                      {errors.country && <div className="invalid-feedback">{errors.country}</div>}
                    </div>
                  </div>

                  <div className="col-lg-4">
                    <div className="mb-3">
                      <label className="form-label">State</label>
                      <input
                        type="text"
                        className={`form-control ${errors.state ? 'is-invalid' : ''}`}
                        name="state"
                        value={formData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                        placeholder="State"
                      />
                      {errors.state && <div className="invalid-feedback">{errors.state}</div>}
                    </div>
                  </div>

                  <div className="col-lg-4">
                    <div className="mb-3">
                      <label className="form-label">City</label>
                      <input
                        type="text"
                        className={`form-control ${errors.city ? 'is-invalid' : ''}`}
                        name="city"
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        placeholder="City"
                      />
                      {errors.city && <div className="invalid-feedback">{errors.city}</div>}
                    </div>
                  </div>

                  <div className="col-lg-4">
                    <div className="mb-3">
                      <label className="form-label">Postal Code <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className={`form-control ${errors.postalCode ? 'is-invalid' : ''}`}
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={(e) => handleInputChange('postalCode', e.target.value)}
                        placeholder="Enter postal code"
                      />
                      {errors.postalCode && <div className="invalid-feedback">{errors.postalCode}</div>}
                    </div>
                  </div>

                </div>
              </div>
              
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-link link-secondary" 
                  onClick={onClose} 
                  style={{ pointerEvents: isSaving ? 'none' : 'auto', opacity: isSaving ? 0.6 : 1 }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary" 
                  style={{ pointerEvents: isSaving ? 'none' : 'auto', opacity: isSaving ? 0.6 : 1 }}
                >
                  {isSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Updating...
                    </>
                  ) : (
                    'Update Profile'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditProfileModal;
